/**
 * Application Constants
 * Centralized configuration values
 */

// ============================================
// STATUS COLORS - Monochrome Black/White Theme
// ============================================

export const STATUS_COLORS = {
  // Job Status
  job: {
    pending: { bg: '#f5f5f5', text: '#666666', border: '#e0e0e0' },
    running: { bg: '#1a1a1a', text: '#ffffff', border: '#000000' },
    completed: { bg: '#000000', text: '#ffffff', border: '#000000' },
    completed_with_warnings: { bg: '#d9d9d9', text: '#1a1a1a', border: '#666666' },
    failed: { bg: '#333333', text: '#ffffff', border: '#1a1a1a' },
    cancelled: { bg: '#999999', text: '#ffffff', border: '#666666' },
    timeout: { bg: '#666666', text: '#ffffff', border: '#333333' },
  },

  // Lead Status
  lead: {
    new: { bg: '#000000', text: '#ffffff', border: '#000000' },
    contacted: { bg: '#1a1a1a', text: '#ffffff', border: '#000000' },
    qualified: { bg: '#333333', text: '#ffffff', border: '#1a1a1a' },
    unqualified: { bg: '#999999', text: '#ffffff', border: '#666666' },
    converted: { bg: '#000000', text: '#ffffff', border: '#000000' },
    archived: { bg: '#f5f5f5', text: '#666666', border: '#e0e0e0' },
  },

  // Lead Priority
  priority: {
    low: { bg: '#f5f5f5', text: '#666666', border: '#e0e0e0' },
    medium: { bg: '#999999', text: '#ffffff', border: '#666666' },
    high: { bg: '#333333', text: '#ffffff', border: '#1a1a1a' },
    urgent: { bg: '#000000', text: '#ffffff', border: '#000000' },
  },

  // Listing Status
  listing: {
    active: { bg: '#000000', text: '#ffffff', border: '#000000' },
    inactive: { bg: '#f5f5f5', text: '#666666', border: '#e0e0e0' },
    deleted: { bg: '#999999', text: '#ffffff', border: '#666666' },
    sold: { bg: '#333333', text: '#ffffff', border: '#1a1a1a' },
    rented: { bg: '#333333', text: '#ffffff', border: '#1a1a1a' },
  },

  // AI Recommendation
  ai: {
    hot: { bg: '#000000', text: '#ffffff', border: '#000000' },
    warm: { bg: '#333333', text: '#ffffff', border: '#1a1a1a' },
    cold: { bg: '#999999', text: '#ffffff', border: '#666666' },
    review: { bg: '#f5f5f5', text: '#666666', border: '#e0e0e0' },
  },
} as const;

// ============================================
// JOB STATES
// ============================================

export const JOB_STATES = {
  PENDING: 'pending',
  PAUSED: 'paused',
  RUNNING: 'running',
  COMPLETED: 'completed',
  COMPLETED_WITH_WARNINGS: 'completed_with_warnings',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  TIMEOUT: 'timeout',
} as const;

export const JOB_STATE_LABELS: Record<string, string> = {
  pending: 'Pending',
  paused: 'Paused / can continue',
  running: 'Running',
  completed: 'Completed',
  completed_with_warnings: 'Completed with warnings',
  failed: 'Failed',
  cancelled: 'Cancelled',
  timeout: 'Timeout',
};

// ============================================
// LEAD STATES
// ============================================

export const LEAD_STATES = {
  NEW: 'new',
  CONTACTED: 'contacted',
  QUALIFIED: 'qualified',
  UNQUALIFIED: 'unqualified',
  CONVERTED: 'converted',
  ARCHIVED: 'archived',
} as const;

export const LEAD_STATE_LABELS: Record<string, string> = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  unqualified: 'Unqualified',
  converted: 'Converted',
  archived: 'Archived',
};

export const LEAD_PRIORITY_LABELS: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

export const CALL_RESULT_LABELS: Record<string, string> = {
  no_answer: 'No Answer',
  voicemail: 'Voicemail',
  callback_requested: 'Callback Requested',
  not_interested: 'Not Interested',
  interested: 'Interested',
  viewing_scheduled: 'Viewing Scheduled',
  offer_made: 'Offer Made',
  deal_closed: 'Deal Closed',
  wrong_number: 'Wrong Number',
  do_not_call: 'Do Not Call',
};

export const NEXT_ACTION_LABELS: Record<string, string> = {
  call: 'Call',
  email: 'Email',
  viewing: 'Viewing',
  follow_up: 'Follow Up',
  send_offer: 'Send Offer',
  wait: 'Wait',
  archive: 'Archive',
};

// ============================================
// API ENDPOINTS
// ============================================

export const API_ENDPOINTS = {
  // Listings
  LISTINGS: '/api/v1/results/listings',
  LISTING_DETAIL: (id: string) => `/api/v1/results/listings/${id}`,
  LISTING_FILTER_OPTIONS: '/api/v1/results/filters/options',

  // Leads
  LEADS: '/api/v1/leads',
  LEAD_DETAIL: (id: string) => `/api/v1/leads/${id}`,
  LEAD_STATUS: (id: string) => `/api/v1/leads/${id}/status`,
  LEAD_ASSIGN: (id: string) => `/api/v1/leads/${id}/assign`,
  LEAD_MESSAGES: (id: string) => `/api/v1/leads/${id}/messages`,
  LEAD_MESSAGES_READ: (id: string) => `/api/v1/leads/${id}/messages/read`,
  LEAD_MESSAGE_DETAIL: (leadId: string, messageId: string) => `/api/v1/leads/${leadId}/messages/${messageId}`,
  LEAD_GENERATE_MESSAGE: (id: string) => `/api/v1/leads/${id}/messages/generate`,
  LEAD_SEND_MESSAGE: (id: string) => `/api/v1/leads/${id}/messages/send`,
  LEAD_SYNC_MESSAGES: (id: string) => `/api/v1/leads/${id}/messages/sync`,
  MESSAGING_ACCOUNTS: '/api/v1/leads/messaging/accounts',
  MESSAGING_ACCOUNT_DETAIL: (id: string) => `/api/v1/leads/messaging/accounts/${id}`,
  MESSAGING_ACCOUNT_VALIDATE: (id: string) => `/api/v1/leads/messaging/accounts/${id}/validate`,
  MESSAGING_ACCOUNT_IMPORT_SESSION: (id: string) => `/api/v1/leads/messaging/accounts/${id}/import-session`,
  MESSAGING_ACCOUNT_LOGIN_BOOTSTRAP: (id: string) => `/api/v1/leads/messaging/accounts/${id}/login-bootstrap`,
  MESSAGING_ACCOUNT_CONNECT_SESSION: (id: string) => `/api/v1/leads/messaging/accounts/${id}/connect-session`,
  MESSAGING_CONNECT_SESSION: (id: string) => `/api/v1/leads/messaging/connect-sessions/${id}`,
  MESSAGING_CONNECT_SESSION_COMPLETE: (token: string) => `/api/v1/leads/messaging/connect-sessions/${token}/complete`,
  MESSAGING_CONNECT_SESSION_CANCEL: (id: string) => `/api/v1/leads/messaging/connect-sessions/${id}/cancel`,

  // Jobs
  JOBS: '/api/v1/jobs',
  JOB_DETAIL: (id: string) => `/api/v1/jobs/${id}`,
  JOB_START: (id: string) => `/api/v1/jobs/${id}/start`,
  JOB_PAUSE: (id: string) => `/api/v1/jobs/${id}/pause`,
  JOB_RESUME: (id: string) => `/api/v1/jobs/${id}/resume`,
  JOB_RESTART: (id: string) => `/api/v1/jobs/${id}/restart`,

  // Profiles
  PROFILES: '/api/v1/profiles',
  AUTOMATION_PROFILE: '/api/v1/profiles/automation',
  PROFILE_DETAIL: (id: string) => `/api/v1/profiles/${id}`,

  // Auth
  AUTH_LOGIN: '/api/auth/login',
  AUTH_LOGOUT: '/api/auth/logout',
  AUTH_REFRESH: '/api/auth/refresh',
  AUTH_ME: '/api/auth/me',

  // Health
  HEALTH: '/api/v1/system/health',
} as const;

// ============================================
// PAGINATION DEFAULTS
// ============================================

export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100,
  LIMIT_OPTIONS: [10, 20, 50, 100],
} as const;

// ============================================
// WEBSOCKET
// ============================================

export const WEBSOCKET = {
  RECONNECT_INTERVAL: 3000,
  MAX_RECONNECT_ATTEMPTS: 5,
  HEARTBEAT_INTERVAL: 30000,
} as const;

// ============================================
// UI CONSTANTS
// ============================================

export const UI = {
  SIDEBAR_WIDTH: 280,
  SIDEBAR_COLLAPSED_WIDTH: 72,
  HEADER_HEIGHT: 64,
  TOAST_DURATION: 5000,
  MODAL_ANIMATION_DURATION: 200,
} as const;

// ============================================
// CRAWL STRATEGIES
// ============================================

export const CRAWL_STRATEGIES = {
  SEQUENTIAL: 'sequential',
  PARALLEL: 'parallel',
  ADAPTIVE: 'adaptive',
} as const;

export const CRAWL_STRATEGY_LABELS: Record<string, string> = {
  sequential: 'Sequential',
  parallel: 'Parallel',
  adaptive: 'Adaptive',
};

// ============================================
// LISTING TYPES
// ============================================

export const LISTING_TYPE_LABELS: Record<string, string> = {
  rent: 'Rent',
  sale: 'Sale',
  unknown: 'Unknown',
};

export const OFFERED_BY_LABELS: Record<string, string> = {
  private: 'Private',
  agency: 'Agency',
  unknown: 'Unknown',
};

// ============================================
// CONDITION LABELS
// ============================================

export const CONDITION_LABELS: Record<string, string> = {
  new: 'New',
  renovated: 'Renovated',
  good: 'Good',
  needs_renovation: 'Needs Renovation',
  unknown: 'Unknown',
};

// ============================================
// ENERGY RATING LABELS
// ============================================

export const ENERGY_RATING_LABELS: Record<string, string> = {
  A: 'A (Best)',
  B: 'B',
  C: 'C',
  D: 'D',
  E: 'E',
  F: 'F',
  G: 'G (Worst)',
  unknown: 'Unknown',
};

// ============================================
// ERROR MESSAGES
// ============================================

export const ERROR_MESSAGES = {
  GENERIC: 'An error occurred. Please try again.',
  NETWORK: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  VALIDATION: 'Please check your input and try again.',
  SERVER: 'Server error. Please try again later.',
} as const;

// ============================================
// SUCCESS MESSAGES
// ============================================

export const SUCCESS_MESSAGES = {
  CREATED: 'Created successfully.',
  UPDATED: 'Updated successfully.',
  DELETED: 'Deleted successfully.',
  SAVED: 'Saved successfully.',
  COPIED: 'Copied to clipboard.',
} as const;
